import { ListSkeleton } from '@atlas/ui-components'
import { Badge, Button } from '@atlas/ui-primitives'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import {
	type AxisValueDTO,
	classificationApi,
	type FacetFilter,
	type FacetValue,
} from '@/services/classification.service'

/**
 * Facet Explorer — a throwaway dev harness to verify the hierarchy + live counts
 * end to end (NOT the polished designer UI). It walks a fixed designer nav path
 * (Region → Country → BU → Brand → Product); clicking a value advances to the
 * next step, re-querying that step's axis filtered by everything picked so far.
 * The product leaf reads the product page for the value it landed on.
 */

interface Step {
	label: string
	axisCode: string
	// For geography, which closure depth this step shows: 'region' = children of
	// the global root; 'country' = children of the picked region. Other axes: 'flat'.
	depth: 'region' | 'country' | 'flat'
	objectType?: string
	// What each value's count means: the DISTINCT next-level entity reachable
	// under it (countBy = that axis; countNoun = the label word). The leaf has none.
	countBy?: string
	countNoun?: string
	// An object only counts if it carries a tag on every listed axis (e.g. a
	// region only counts a country if that country has a brand-carrying object).
	requireAxes?: string[]
}

// Each level counts the distinct NEXT-level entity: a region shows how many
// countries with active brands, a country how many brands, a brand how many
// products, etc. (configurable per step via countBy/countNoun).
const NAV_PATH: readonly Step[] = [
	// Region counts distinct COUNTRIES that have a brand-carrying object (requireAxes: brand).
	{
		label: 'Region',
		axisCode: 'geography',
		depth: 'region',
		countBy: 'geography',
		countNoun: 'active countries',
		requireAxes: ['brand'],
	},
	{ label: 'Country', axisCode: 'geography', depth: 'country', countBy: 'brand', countNoun: 'active brands' },
	{ label: 'Business Unit', axisCode: 'business_unit', depth: 'flat', countBy: 'brand', countNoun: 'active brands' },
	// Brands are the ROOTS of the brand axis and products are their children — one axis,
	// two levels — so the Brand step counts by that axis and the Product step drills into
	// it rather than into a `product` axis of its own.
	{ label: 'Brand', axisCode: 'brand', depth: 'region', countBy: 'brand', countNoun: 'active products' },
	// Product counts distinct ASSETS (files) tagged to each product under the
	// filter. NOT objectType:'product' — the product OBJECT isn't geo-tagged, so
	// restricting to it would wrongly exclude everything under a geo filter.
	{ label: 'Product', axisCode: 'brand', depth: 'country', countNoun: 'assets' },
]

interface Crumb {
	step: number
	axisCode: string
	valueCode: string
	label: string
}

export function FacetExplorerPage() {
	const [crumbs, setCrumbs] = useState<Crumb[]>([])
	const [productCode, setProductCode] = useState<string | null>(null)

	const stepIndex = Math.min(crumbs.length, NAV_PATH.length - 1)
	const step = NAV_PATH[stepIndex]
	const isLeaf = stepIndex === NAV_PATH.length - 1

	const axesQuery = useQuery({ queryKey: ['axes'], queryFn: classificationApi.listAxes })
	const currentAxis = (axesQuery.data ?? []).find((a) => a.code === step.axisCode)

	// Accumulated filter from the breadcrumb. Hierarchical drilldowns (Region →
	// Country on the same `geography` axis) must keep only the MOST-SPECIFIC value
	// per axis — crumbs are ordered oldest→newest, so the last crumb for an axis
	// wins. Appending instead would OR the values (geography ['emea','es']) and the
	// facet would keep returning the whole region rather than narrowing to Spain.
	const filter: FacetFilter = crumbs.reduce<FacetFilter>((acc, c) => {
		acc[c.axisCode] = [c.valueCode]
		return acc
	}, {})

	const facetQuery = useQuery({
		queryKey: ['facet', step.axisCode, filter, step.objectType, step.countBy, step.requireAxes],
		queryFn: () =>
			classificationApi.facet(step.axisCode, filter, {
				objectType: step.objectType,
				countBy: step.countBy,
				requireAxes: step.requireAxes,
			}),
	})

	// Geography steps need parentId to split region vs country — fetch the axis
	// values (facet returns flat values+counts without parent info). Also used to
	// show empty regions at the Region entry.
	const valuesQuery = useQuery({
		queryKey: ['axis-values', currentAxis?.axisId],
		queryFn: () => classificationApi.listValues(currentAxis?.axisId ?? ''),
		// Any step that filters by DEPTH needs the value tree, not just geography: brand and
		// product are two levels of one axis and are told apart the same way.
		enabled: !!currentAxis && step.depth !== 'flat',
	})

	const rows = buildRows(step, crumbs, facetQuery.data ?? [], valuesQuery.data ?? [])
	const indent = stepIndex * 20

	const drillInto = (row: FacetValue) => {
		// Product leaf: open the page. The facet returns the product's axis-value
		// CODE; the product page is keyed by product OBJECT id, so ProductPageView
		// resolves code→productId via the catalog list.
		if (isLeaf) {
			setProductCode(row.code)
			return
		}
		setCrumbs((c) => [...c, { step: stepIndex, axisCode: step.axisCode, valueCode: row.code, label: row.label }])
		setProductCode(null)
	}
	const popTo = (index: number) => {
		setCrumbs((c) => c.slice(0, index))
		setProductCode(null)
	}

	return (
		<div style={{ padding: 24, maxWidth: 900 }}>
			<h1>Facet Explorer (dev harness)</h1>

			<PathStepper current={stepIndex} />

			{/* Breadcrumb of the accumulated filter (click to pop back) */}
			<div style={{ margin: '12px 0', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
				<Button onPress={() => popTo(0)} isDisabled={crumbs.length === 0}>
					Reset
				</Button>
				{crumbs.map((c, i) => (
					<Button key={`${c.axisCode}:${c.valueCode}`} onPress={() => popTo(i + 1)}>
						{NAV_PATH[c.step].label}: {c.label} ✕
					</Button>
				))}
			</div>

			{/* Current step: value list, indented by depth, labeled by axis/level */}
			<div style={{ border: '1px solid #e5e5e5', borderRadius: 8, padding: 16 }}>
				<h2 style={{ margin: '0 0 4px', fontSize: 16 }}>
					{step.label}{' '}
					<span style={{ fontWeight: 400, color: '#888', fontSize: 13 }}>
						— {currentAxis?.name ?? step.axisCode}
						{currentAxis ? ` (${currentAxis.kind})` : ''}
					</span>
				</h2>
				<p style={{ margin: '0 0 12px', color: '#888', fontSize: 13 }}>
					each {step.label.toLowerCase()} shows its <strong>{step.countNoun ?? 'count'}</strong> ·{' '}
					{rows.filter((r) => r.count > 0).length} active · {rows.length} shown
				</p>

				{facetQuery.isLoading && <ListSkeleton rows={3} label="Loading facets" />}
				{facetQuery.isError && <p style={{ color: 'crimson' }}>Error loading facet (is the backend deployed?).</p>}
				{!facetQuery.isLoading && rows.length === 0 && <p>No values at this level under the current filter.</p>}

				<ValueList
					rows={rows}
					indent={indent}
					crumbLabels={crumbs.map((c) => c.label)}
					isProductLeaf={isLeaf}
					onDrill={drillInto}
				/>
			</div>

			{productCode && <ProductPageView productCode={productCode} />}
		</div>
	)
}

/** Nav-path stepper — shows Region→Country→BU→Brand→Product and where we are. */
function PathStepper({ current }: { current: number }) {
	return (
		<div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', margin: '12px 0' }}>
			{NAV_PATH.map((p, i) => (
				<span key={p.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
					<span
						style={{
							padding: '4px 10px',
							borderRadius: 6,
							fontSize: 13,
							background: i === current ? '#111' : i < current ? '#4f7' : '#eee',
							color: i === current ? '#fff' : '#555',
						}}
					>
						{p.label}
					</span>
					{i < NAV_PATH.length - 1 && <span style={{ color: '#bbb' }}>→</span>}
				</span>
			))}
		</div>
	)
}

/** The indented value list for the current step (each row drills or opens). */
function ValueList({
	rows,
	indent,
	crumbLabels,
	isProductLeaf,
	onDrill,
}: {
	rows: FacetValue[]
	indent: number
	crumbLabels: string[]
	isProductLeaf: boolean
	onDrill: (row: FacetValue) => void
}) {
	return (
		<ul style={{ listStyle: 'none', padding: 0, margin: 0, paddingLeft: indent }}>
			{indent > 0 && (
				<li style={{ color: '#bbb', fontSize: 12, marginBottom: 4 }}>↳ under {crumbLabels.join(' › ')}</li>
			)}
			{rows.map((v) => {
				const empty = v.count === 0
				return (
					<li key={v.valueId} style={{ margin: '4px 0', opacity: empty ? 0.55 : 1 }}>
						<Button onPress={() => onDrill(v)} isDisabled={empty}>
							{v.label} <Badge>{v.count}</Badge>
						</Button>
						{empty && <span style={{ marginLeft: 8, color: '#999', fontSize: 12 }}>no objects yet</span>}
						{isProductLeaf && !empty && (
							<span style={{ marginLeft: 8, color: '#888', fontSize: 12 }}>← click to open</span>
						)}
					</li>
				)
			})}
		</ul>
	)
}

/**
 * Decide the rows for a step: merge facet counts onto the right value set.
 *  - Region: geography values whose parent is the global root (regions), incl. 0.
 *  - Country: geography values whose parent is the picked region.
 *  - flat axes: the facet's active values as-is (only values with objects).
 */
function buildRows(step: Step, crumbs: Crumb[], facet: FacetValue[], values: AxisValueDTO[]): FacetValue[] {
	const countByCode = new Map(facet.map((v) => [v.code, v]))
	// A flat step shows whatever the facet returned; a depth-filtered one picks its level
	// out of the value tree. Keyed on `depth`, not on the axis code — brand and product are
	// two levels of ONE axis, so an axis-code test cannot tell those two steps apart.
	if (step.depth === 'flat') return facet

	// Pick this depth's values out of the tree by `parentId`.
	const byId = new Map(values.map((v) => [v.valueId, v]))
	const codeOf = (id: string | null) => (id ? (byId.get(id)?.code ?? null) : null)

	let atDepth: AxisValueDTO[]
	if (step.depth === 'region') {
		// regions = values whose parent is the global root (or no parent).
		atDepth = values.filter((v) => {
			const parentCode = codeOf(v.parentId)
			return parentCode === 'global' || v.parentId === null
		})
		// drop the global root itself from the region list if present
		atDepth = atDepth.filter((v) => v.code !== 'global')
	} else {
		// country = children of the region picked in the previous crumb.
		const region = crumbs[crumbs.length - 1]?.valueCode
		atDepth = values.filter((v) => codeOf(v.parentId) === region)
	}
	return atDepth.map((v) => countByCode.get(v.code) ?? { valueId: v.valueId, code: v.code, label: v.label, count: 0 })
}

/** The parts of the product-page read this harness reports on. */
interface HarnessProductPage {
	resolvedName?: string
	brand?: { name?: string } | null
	market?: { label?: string } | null
	appliesTo?: Record<string, string[]>
	rules?: unknown[]
}

/**
 * Product leaf — confirms the drilled-to product VALUE is backed by a product object,
 * and reports what the product-page read answers for it: the market-resolved name, the
 * brand, and the ladder it resolved for per axis. Receives the product's axis-value CODE
 * (what the facet returns) and resolves it to the product OBJECT id via the catalog list,
 * since the product-page endpoint is keyed by productId.
 *
 * No asset list: the read carries none by design — an `ObjectRef` cannot render an asset,
 * so the delivered assets come from file discovery, which the real product page runs. This
 * harness ends at the resolution it exists to verify and links there for the assets.
 */
export function ProductPageView({ productCode }: { productCode: string }) {
	const productsQuery = useQuery({ queryKey: ['products'], queryFn: classificationApi.listProducts })
	const productId = productsQuery.data?.find((p) => p.code === productCode)?.productId

	const { data, isLoading, isError } = useQuery({
		queryKey: ['product-page', productId],
		queryFn: () => classificationApi.getProductPage(productId ?? ''),
		enabled: !!productId,
	})
	if (productsQuery.isLoading || isLoading) return <ListSkeleton rows={4} label="Loading product page" />
	if (productsQuery.data && !productId)
		return <p style={{ color: 'crimson' }}>No product object for “{productCode}” (value has no object row).</p>
	if (isError) return <p style={{ color: 'crimson' }}>Product page not found (drift → 404).</p>

	const page = (data ?? {}) as HarnessProductPage
	const appliesTo = Object.entries(page.appliesTo ?? {})
	return (
		<div style={{ marginTop: 16, border: '1px solid #e5e5e5', borderRadius: 8, padding: 16 }}>
			<h3 style={{ margin: '0 0 8px', fontSize: 15 }}>Product page — {page.resolvedName ?? productCode}</h3>
			<div style={{ fontSize: 13 }}>
				<div>
					<span style={{ color: '#888' }}>brand:</span> {page.brand?.name ?? '—'}
				</div>
				<div>
					<span style={{ color: '#888' }}>resolved market:</span> {page.market?.label ?? 'unscoped'}
				</div>
				<div>
					<span style={{ color: '#888' }}>matching rules:</span> {page.rules?.length ?? 0}
				</div>
			</div>
			<h4 style={{ margin: '12px 0 4px', fontSize: 13 }}>Applies to</h4>
			{appliesTo.length === 0 ? (
				<p style={{ color: '#888', fontSize: 13, margin: 0 }}>Nothing resolved on any axis.</p>
			) : (
				appliesTo.map(([axisCode, path]) => (
					<div key={axisCode} style={{ marginLeft: 16, fontSize: 13 }}>
						<span style={{ color: '#888' }}>{axisCode}:</span> {path.join(' ← ')}
					</div>
				))
			)}
			{/* Full page load, deliberately: the harness is router-independent. */}
			<p style={{ margin: '12px 0 0', fontSize: 13 }}>
				<a href={`/explore/product/${productId}`}>Open the product page</a> for its delivered assets — they come from
				file discovery, not from this read.
			</p>
			<details style={{ marginTop: 8 }}>
				<summary style={{ cursor: 'pointer', fontSize: 12, color: '#888' }}>raw product page JSON</summary>
				<pre style={{ background: '#f5f5f5', padding: 12, overflow: 'auto' }}>{JSON.stringify(data, null, 2)}</pre>
			</details>
		</div>
	)
}
